import { Box, Grid, Typography } from '@mui/material';
import { Alert, backendServices, Loading, Card, Button } from 'common';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useCompanySubmissionMethods } from '../../../services';
import { routes } from '../../../signup-portal.routes';
import { SubmissionMethodCard } from './SubmissionMethodCard';

function SubmissionMethodsFetcher({
    companyId,
    children,
}: {
    companyId: number;
    children: (methods: backendServices.ViewModels.SubmissionMethodViewModel[]) => React.ReactNode;
}) {
    const {
        data: methods = [],
        isLoading,
        error,
    } = useCompanySubmissionMethods({
        companyId,
    });

    if (isLoading) {
        return (
            <Box>
                <Loading />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert testId="error-loading-submission-methods" severity="error">
                We were unable to retrieve the submission methods.
            </Alert>
        );
    }

    return <>{children(methods)}</>;
}

function SubmissionMethodSelectorContent({
    companyToken,
    companyName,
    threshold,
    methods,
}: {
    companyToken: string;
    companyName: string;
    threshold: number;
    methods: backendServices.ViewModels.SubmissionMethodViewModel[];
}) {
    const history = useHistory();

    const redirectToSignUpForm = useCallback(
        (
            submissionMethod:
                | backendServices.ViewModels.SubmissionMethodViewModel
                | backendServices.ViewModels.SubmissionMethod.WebEntry
        ) => {
            history.push(`${routes.supplierSignUpForm.path}`.replace(':companyToken', companyToken), {
                submissionMethod,
            });
        },
        [history, companyToken]
    );

    const filteredAndSortedMethods = useMemo(
        () =>
            methods
                .filter(({ SubmissionMethod }) =>
                    [
                        backendServices.ViewModels.SubmissionMethod.WebEntry,
                        backendServices.ViewModels.SubmissionMethod.Email,
                        backendServices.ViewModels.SubmissionMethod.VirtualPrinter,
                        backendServices.ViewModels.SubmissionMethod.EDI_XML,
                    ].includes(SubmissionMethod)
                )
                .sort((a, b) => b.SubmissionMethod - a.SubmissionMethod),
        [methods]
    );

    const isWebEntrySelectable = filteredAndSortedMethods.some(
        ({ SubmissionMethod }) => SubmissionMethod === backendServices.ViewModels.SubmissionMethod.WebEntry
    );

    const [exceedsThreshold, setExceedsThreshold] = useState(!isWebEntrySelectable);

    const handleBelowThresholdClick = useCallback(() => {
        redirectToSignUpForm(backendServices.ViewModels.SubmissionMethod.WebEntry);
    }, [redirectToSignUpForm]);

    return (
        <Box>
            {isWebEntrySelectable ? (
                <Typography variant="h5" component="h2" textAlign="center" mb={3}>
                    How many documents do you send to {companyName} annually?
                </Typography>
            ) : (
                <Typography variant="h5" component="h2" textAlign="center" mb={3}>
                    {filteredAndSortedMethods.length > 1 ? 'Choose a submission method' : 'Submission method'}
                </Typography>
            )}
            {isWebEntrySelectable && (
                <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
                    <Grid item xs={12} sm={6}>
                        <Card
                            rootProps={{
                                sx: {
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: 1,
                                    opacity: exceedsThreshold ? 0.6 : 1,
                                    transition: 'opacity 0.2s',
                                },
                            }}
                            contentProps={{
                                sx: {
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    p: 4,
                                },
                            }}
                            content={
                                <>
                                    <Box
                                        sx={{
                                            maxWidth: { xs: 360, sm: 'none' },
                                            width: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <Typography variant="body1" component="p" textAlign="center">
                                            Fewer than {threshold} documents per year
                                        </Typography>
                                    </Box>
                                    <Button
                                        onClick={handleBelowThresholdClick}
                                        sx={{ minWidth: 200, mt: 1 }}
                                        disabled={exceedsThreshold}
                                    >
                                        Register with Web Entry
                                    </Button>
                                </>
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Card
                            rootProps={{
                                sx: {
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxShadow: exceedsThreshold ? 3 : 1,
                                    border: (t) =>
                                        exceedsThreshold
                                            ? `2px solid ${t.palette.primary.main}`
                                            : '2px solid transparent',
                                    transition: 'box-shadow 0.2s, border-color 0.2s',
                                },
                            }}
                            contentProps={{
                                sx: {
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    p: 4,
                                },
                            }}
                            content={
                                <>
                                    <Box
                                        sx={{
                                            maxWidth: { xs: 360, sm: 'none' },
                                            width: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <Typography variant="body1" component="p" textAlign="center">
                                            More than {threshold} documents per year
                                        </Typography>
                                    </Box>
                                    <Button
                                        onClick={() => setExceedsThreshold((prev) => !prev)}
                                        sx={{ minWidth: 200, mt: 1 }}
                                    >
                                        Choose Delivery Method
                                    </Button>
                                </>
                            }
                        />
                    </Grid>
                </Grid>
            )}

            {exceedsThreshold && (
                <Grid
                    container
                    spacing={3}
                    alignItems="stretch"
                    sx={[
                        { mt: { xs: 2, md: 4 } },
                        filteredAndSortedMethods.length < 2 && { justifyContent: 'space-around' },
                    ]}
                >
                    {filteredAndSortedMethods.map((method) => (
                        <Grid item xs={12} md={6} lg={4} xl={3} key={`${method.Id} - ${method.Name}`}>
                            <SubmissionMethodCard method={method} onButtonClick={redirectToSignUpForm} />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}

export function SubmissionMethodSelector({
    companyToken,
    companyId,
    companyName,
    threshold,
}: {
    companyToken: string;
    companyId: number;
    companyName: string;
    threshold: number;
}) {
    return (
        <SubmissionMethodsFetcher companyId={companyId}>
            {(methods) => (
                <SubmissionMethodSelectorContent
                    companyToken={companyToken}
                    companyName={companyName}
                    threshold={threshold}
                    methods={methods}
                />
            )}
        </SubmissionMethodsFetcher>
    );
}
